import { useCallback, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { defaultFormState, defaultRecordFormValues, pageSizeSelectOptions, sourceOptions, tagOptions } from "../config";
import { mySqlQueryListRepo } from "../repo/mySqlQueryListRepo";
import {
  buildMySqlQueryListParams,
  buildMySqlQueryListQueryKey,
  buildPaginationItems,
  cloneFormState,
  formatAmount,
  getDeleteErrorMessage,
  getDeleteFallbackPage,
  isSameFormState,
  mapRecordFormValuesToPayload,
  mapRecordToFormValues,
} from "../service/mySqlQueryListService";
import type {
  MySqlQueryListItem,
  MySqlQueryListPage,
  MySqlQueryListParams,
  MySqlQueryRecordFormValues,
  QueryFormState,
  RecordModalMode,
} from "../types";
import { useMySqlQueryListQuery } from "./useMySqlQueryListQuery";
import { useViewportMode } from "./useViewportMode";

type UseMySqlQueryListRuntimeOptions = {
  queryRootKey?: readonly string[];
  fetchList?: (params: MySqlQueryListParams) => Promise<MySqlQueryListPage>;
  createRecord?: (payload: Parameters<typeof mySqlQueryListRepo.createRecord>[0]) => ReturnType<typeof mySqlQueryListRepo.createRecord>;
  updateRecord?: (
    recordId: Parameters<typeof mySqlQueryListRepo.updateRecord>[0],
    payload: Parameters<typeof mySqlQueryListRepo.updateRecord>[1],
  ) => ReturnType<typeof mySqlQueryListRepo.updateRecord>;
  deleteRecord?: (recordId: Parameters<typeof mySqlQueryListRepo.deleteRecord>[0]) => ReturnType<typeof mySqlQueryListRepo.deleteRecord>;
};

/** MySQL 查询列表运行时状态。 */
export function useMySqlQueryListRuntime(options: UseMySqlQueryListRuntimeOptions = {}) {
  const queryClient = useQueryClient();
  const [formState, setFormState] = useState<QueryFormState>(defaultFormState);
  const [submittedFilters, setSubmittedFilters] = useState<QueryFormState>(defaultFormState);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const viewportMode = useViewportMode();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [errorMessage, setErrorMessage] = useState("");
  const [recordModalVisible, setRecordModalVisible] = useState(false);
  const [recordModalMode, setRecordModalMode] = useState<RecordModalMode>("create");
  const [activeRecord, setActiveRecord] = useState<MySqlQueryListItem | null>(null);

  const query = useMySqlQueryListQuery({
    page,
    pageSize,
    filters: submittedFilters,
    queryRootKey: options.queryRootKey,
    fetchList: options.fetchList,
  });

  const items = query.items;
  const total = query.total;
  const loading = query.isPending || query.isFetching;
  const currentPage = query.data?.page ?? page;
  const currentPageSize = query.data?.pageSize ?? pageSize;
  const isCompact = viewportMode !== "desktop";
  const totalPages = Math.max(1, Math.ceil(total / currentPageSize));
  const queryErrorMessage = query.error instanceof Error ? query.error.message : "";
  const displayErrorMessage = errorMessage || queryErrorMessage;

  const recordModalInitialValues = useMemo(
    () => (activeRecord ? mapRecordToFormValues(activeRecord) : defaultRecordFormValues),
    [activeRecord],
  );

  const paginationItems = useMemo(
    () => buildPaginationItems(currentPage, totalPages, viewportMode === "mobile"),
    [currentPage, totalPages, viewportMode],
  );

  const prefetchListData = useCallback(
    async (
      nextPage: number,
      nextPageSize: number,
      nextFilters: QueryFormState,
    ): Promise<MySqlQueryListPage> => {
      const requestState = {
        page: nextPage,
        pageSize: nextPageSize,
        filters: nextFilters,
      };

      return queryClient.fetchQuery({
        queryKey: buildMySqlQueryListQueryKey(requestState, options.queryRootKey),
        queryFn: () =>
          (options.fetchList ?? mySqlQueryListRepo.fetchList)(buildMySqlQueryListParams(requestState)),
        retry: false,
        staleTime: 0,
      });
    },
    [options.fetchList, options.queryRootKey, queryClient],
  );

  const createMutation = useMutation({
    mutationFn: async (values: MySqlQueryRecordFormValues) =>
      (options.createRecord ?? mySqlQueryListRepo.createRecord)(mapRecordFormValuesToPayload(values)),
    onSuccess: async () => {
      setErrorMessage("");
      setRecordModalVisible(false);
      setActiveRecord(null);
      setRecordModalMode("create");
      await prefetchListData(1, currentPageSize, submittedFilters);
      setPage(1);
    },
    onError: (error) => {
      setErrorMessage(error instanceof Error ? error.message : "提交失败，请稍后重试。");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ recordId, values }: { recordId: number; values: MySqlQueryRecordFormValues }) =>
      (options.updateRecord ?? mySqlQueryListRepo.updateRecord)(recordId, mapRecordFormValuesToPayload(values)),
    onSuccess: async () => {
      setErrorMessage("");
      setRecordModalVisible(false);
      setActiveRecord(null);
      setRecordModalMode("create");
      await prefetchListData(currentPage, currentPageSize, submittedFilters);
    },
    onError: (error) => {
      setErrorMessage(error instanceof Error ? error.message : "提交失败，请稍后重试。");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (item: MySqlQueryListItem) =>
      (options.deleteRecord ?? mySqlQueryListRepo.deleteRecord)(item.id),
    onSuccess: async () => {
      setErrorMessage("");
      const fallbackPage = getDeleteFallbackPage(currentPage, items.length);
      await prefetchListData(fallbackPage, currentPageSize, submittedFilters);
      if (fallbackPage !== currentPage) {
        setPage(fallbackPage);
      }
    },
    onError: (error) => {
      setErrorMessage(getDeleteErrorMessage(error));
    },
  });

  const recordSubmitting = createMutation.isPending || updateMutation.isPending;
  const deleteSubmitting = deleteMutation.isPending;

  const handleSearch = useCallback((): void => {
    setErrorMessage("");
    const nextFilters = cloneFormState(formState);

    if (currentPage === 1 && isSameFormState(nextFilters, submittedFilters)) {
      void query.refetch();
      return;
    }

    setSubmittedFilters(nextFilters);
    setPage(1);
  }, [currentPage, formState, query, submittedFilters]);

  const handleReset = useCallback((): void => {
    setErrorMessage("");

    if (currentPage === 1 && isSameFormState(defaultFormState, submittedFilters)) {
      setFormState(defaultFormState);
      void query.refetch();
      return;
    }

    setFormState(defaultFormState);
    setSubmittedFilters(defaultFormState);
    setPage(1);
  }, [currentPage, query, submittedFilters]);

  const handlePageChange = useCallback(
    (nextPage: number): void => {
      if (nextPage < 1 || nextPage > totalPages || nextPage === currentPage) {
        return;
      }

      setPage(nextPage);
    },
    [currentPage, totalPages],
  );

  const handlePageSizeChange = useCallback(
    (nextPageSize: number): void => {
      if (nextPageSize === currentPageSize) {
        return;
      }

      setPage(1);
      setPageSize(nextPageSize);
    },
    [currentPageSize],
  );

  const handleOpenCreateModal = useCallback((): void => {
    setErrorMessage("");
    setRecordModalMode("create");
    setActiveRecord(null);
    setRecordModalVisible(true);
  }, []);

  const handleOpenEditModal = useCallback((item: MySqlQueryListItem): void => {
    setErrorMessage("");
    setRecordModalMode("edit");
    setActiveRecord(item);
    setRecordModalVisible(true);
  }, []);

  const handleCloseRecordModal = useCallback((): void => {
    if (recordSubmitting) {
      return;
    }
    setRecordModalVisible(false);
    setActiveRecord(null);
    setRecordModalMode("create");
  }, [recordSubmitting]);

  const handleSubmitRecordModal = useCallback(
    async (values: MySqlQueryRecordFormValues): Promise<void> => {
      if (recordModalMode === "create") {
        await createMutation.mutateAsync(values);
        return;
      }

      if (!activeRecord) {
        setErrorMessage("未找到可编辑的记录。");
        return;
      }

      await updateMutation.mutateAsync({ recordId: activeRecord.id, values });
    },
    [activeRecord, createMutation, recordModalMode, updateMutation],
  );

  const handleConfirmDelete = useCallback(
    async (item: MySqlQueryListItem): Promise<void> => {
      if (deleteSubmitting) {
        return;
      }

      await deleteMutation.mutateAsync(item);
    },
    [deleteMutation, deleteSubmitting],
  );

  return {
    activeRecord,
    currentPage,
    currentPageSize,
    deleteSubmitting,
    displayErrorMessage,
    formatAmount,
    formState,
    isCompact,
    items,
    loading,
    pageSize,
    paginationItems,
    pageSizeSelectOptions,
    recordModalInitialValues,
    recordModalMode,
    recordModalVisible,
    recordSubmitting,
    showAdvancedFilters,
    total,
    totalPages,
    viewportMode,
    sourceOptions,
    tagOptions,
    setFormState,
    setShowAdvancedFilters,
    handleCloseRecordModal,
    handleConfirmDelete,
    handleOpenCreateModal,
    handleOpenEditModal,
    handlePageChange,
    handlePageSizeChange,
    handleReset,
    handleSearch,
    handleSubmitRecordModal,
  };
}
